import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Save, Loader2, Sparkles, Bot, MessageSquare, Flame, ListChecks, Send, X,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { koraAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

/**
 * Kora training / configuration panel.
 *
 * KR-admin-only page for teaching Kora — the public landing-page
 * chatbot — how KR wants it to sound and what info to always collect.
 * Backed by `settings.kora_training_config`. Changes apply to any
 * new visitor conversation started after saving.
 */
export default function KoraTrainingPage() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Preview widget state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLang, setPreviewLang] = useState('en');
  const [previewInput, setPreviewInput] = useState('');
  const [previewLog, setPreviewLog] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await koraAPI.getTrainingConfig();
        setCfg(data);
      } catch (e) {
        toast.error(getErrorMessage(e, 'Failed to load Kora training config'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k, v) => {
    setCfg((c) => ({ ...c, [k]: v }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        persona_en: cfg.persona_en || '',
        persona_es: cfg.persona_es || '',
        custom_instructions_en: cfg.custom_instructions_en || '',
        custom_instructions_es: cfg.custom_instructions_es || '',
        greeting_en: cfg.greeting_en || '',
        greeting_es: cfg.greeting_es || '',
        urgency_keywords: cfg.urgency_keywords || [],
        required_qualification_fields: cfg.required_qualification_fields || [],
        enabled: cfg.enabled !== false,
      };
      const { data } = await koraAPI.updateTrainingConfig(payload);
      setCfg(data);
      setDirty(false);
      toast.success('Kora training saved — applies to new conversations.');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to save Kora training'));
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    const text = previewInput.trim();
    if (!text) return;
    setPreviewLoading(true);
    const nextLog = [...previewLog, { role: 'user', text }];
    setPreviewLog(nextLog);
    setPreviewInput('');
    try {
      const { data } = await koraAPI.previewTraining({
        message: text,
        language: previewLang,
        draft: {
          persona_en: cfg.persona_en || '',
          persona_es: cfg.persona_es || '',
          custom_instructions_en: cfg.custom_instructions_en || '',
          custom_instructions_es: cfg.custom_instructions_es || '',
          greeting_en: cfg.greeting_en || '',
          greeting_es: cfg.greeting_es || '',
          urgency_keywords: cfg.urgency_keywords || [],
          required_qualification_fields: cfg.required_qualification_fields || [],
          enabled: cfg.enabled !== false,
        },
      });
      // Strip the trailing JSON block so the preview stays readable.
      const visible = String(data?.reply || '').replace(/```json[\s\S]*?```/g, '').trim();
      setPreviewLog((l) => [...l, { role: 'assistant', text: visible || '(empty reply)' }]);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Preview failed'));
      setPreviewLog((l) => [...l, { role: 'assistant', text: '(preview failed — check your Emergent LLM key)' }]);
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading || !cfg) {
    return <p className="p-10 text-center text-sm text-slate-500">Loading…</p>;
  }

  return (
    <div data-testid="kora-training-page">
      <PageHeader
        title="Kora Training"
        description="Teach Kora how you want her to greet visitors, what to collect, and when to escalate a lead as urgent."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => { setPreviewOpen((v) => !v); setPreviewLog([]); }}
              data-testid="toggle-kora-preview-btn"
            >
              <Sparkles size={14} className="mr-1.5" />
              {previewOpen ? 'Close preview' : 'Try Kora'}
            </Button>
            <Button
              onClick={save}
              disabled={!dirty || saving}
              className="bg-[#0f2a5e] hover:bg-[#0a1f47]"
              data-testid="save-kora-training-btn"
            >
              {saving ? (
                <><Loader2 size={14} className="mr-1.5 animate-spin" /> Saving…</>
              ) : (
                <><Save size={14} className="mr-1.5" /> Save changes</>
              )}
            </Button>
          </div>
        }
      />

      <div className="page-content grid gap-4 lg:grid-cols-3">
        <div className={previewOpen ? 'lg:col-span-2 space-y-4' : 'lg:col-span-3 space-y-4'}>

          {/* Enabled toggle */}
          <Card>
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Bot size={16} className="text-[#b8862d]" /> Chatbot enabled
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  When off, landing pages fall back to the static form only — Kora will not appear.
                </p>
              </div>
              <Switch
                checked={cfg.enabled !== false}
                onCheckedChange={(v) => set('enabled', v)}
                data-testid="kora-enabled-switch"
              />
            </CardContent>
          </Card>

          {/* Persona & Tone */}
          <Card>
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2 text-[#b8862d]">
                <MessageSquare size={14} />
                <h3 className="text-xs font-semibold uppercase tracking-wider">
                  Persona &amp; tone
                </h3>
              </div>
              <p className="text-xs text-slate-500">
                Describe Kora&apos;s voice — friendly, professional, bilingual, warm, playful, etc. Kept separate per language.
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Persona (English)</Label>
                  <Textarea
                    rows={4}
                    value={cfg.persona_en || ''}
                    onChange={(e) => set('persona_en', e.target.value)}
                    placeholder="e.g. Warm, professional, calls the visitor by first name once known..."
                    data-testid="kora-persona-en"
                  />
                </div>
                <div>
                  <Label>Persona (Español)</Label>
                  <Textarea
                    rows={4}
                    value={cfg.persona_es || ''}
                    onChange={(e) => set('persona_es', e.target.value)}
                    placeholder="Ej: Cálida, profesional, tutea al visitante una vez que sabe su nombre..."
                    data-testid="kora-persona-es"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Custom instructions */}
          <Card>
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2 text-[#b8862d]">
                <Sparkles size={14} />
                <h3 className="text-xs font-semibold uppercase tracking-wider">
                  Custom instructions
                </h3>
              </div>
              <p className="text-xs text-slate-500">
                Extra guidance on what Kora should ask, avoid, or how to handle common scenarios. Applied on top of the built-in rules.
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Instructions (English)</Label>
                  <Textarea
                    rows={6}
                    value={cfg.custom_instructions_en || ''}
                    onChange={(e) => set('custom_instructions_en', e.target.value)}
                    placeholder={"e.g. Always ask about their current Medicare plan.\nDo not quote premiums.\nMention our free enrollment help when relevant."}
                    data-testid="kora-instructions-en"
                  />
                </div>
                <div>
                  <Label>Instrucciones (Español)</Label>
                  <Textarea
                    rows={6}
                    value={cfg.custom_instructions_es || ''}
                    onChange={(e) => set('custom_instructions_es', e.target.value)}
                    placeholder={"Ej: Siempre pregunta por el plan Medicare actual.\nNo cotices primas.\nMenciona nuestra ayuda gratuita de inscripción cuando aplique."}
                    data-testid="kora-instructions-es"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Greeting */}
          <Card>
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2 text-[#b8862d]">
                <MessageSquare size={14} />
                <h3 className="text-xs font-semibold uppercase tracking-wider">
                  Welcome message
                </h3>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Greeting (English)</Label>
                  <Textarea
                    rows={2}
                    value={cfg.greeting_en || ''}
                    onChange={(e) => set('greeting_en', e.target.value)}
                    data-testid="kora-greeting-en"
                  />
                </div>
                <div>
                  <Label>Saludo (Español)</Label>
                  <Textarea
                    rows={2}
                    value={cfg.greeting_es || ''}
                    onChange={(e) => set('greeting_es', e.target.value)}
                    data-testid="kora-greeting-es"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Required qualification fields */}
          <Card>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center gap-2 text-[#b8862d]">
                <ListChecks size={14} />
                <h3 className="text-xs font-semibold uppercase tracking-wider">
                  Required qualification fields
                </h3>
              </div>
              <p className="text-xs text-slate-500">
                Kora only marks a conversation as &quot;ready for follow-up&quot; once she has captured every field below.
                Note: <span className="font-mono">phone</span> and <span className="font-mono">email</span> are treated as
                &quot;at least one of&quot; unless you require both explicitly.
              </p>
              <TagListEditor
                value={cfg.required_qualification_fields || []}
                onChange={(v) => set('required_qualification_fields', v)}
                placeholder="add field (e.g. state, birthdate)"
                testId="kora-required-fields"
              />
            </CardContent>
          </Card>

          {/* Urgency keywords */}
          <Card>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center gap-2 text-[#b8862d]">
                <Flame size={14} />
                <h3 className="text-xs font-semibold uppercase tracking-wider">
                  Urgency keywords
                </h3>
              </div>
              <p className="text-xs text-slate-500">
                When a visitor uses one of these words, Kora will treat the conversation as high-priority and acknowledge it. Case-insensitive.
              </p>
              <TagListEditor
                value={cfg.urgency_keywords || []}
                onChange={(v) => set('urgency_keywords', v)}
                placeholder="add keyword (e.g. urgent, today)"
                testId="kora-urgency-keywords"
              />
            </CardContent>
          </Card>
        </div>

        {/* Preview column */}
        {previewOpen && (
          <div className="lg:col-span-1">
            <Card className="lg:sticky lg:top-4">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-[#b8862d]">
                    <Sparkles size={14} />
                    <h3 className="text-xs font-semibold uppercase tracking-wider">
                      Preview Kora
                    </h3>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant={previewLang === 'en' ? 'default' : 'outline'}
                      className="h-6 text-[10px] px-2"
                      onClick={() => setPreviewLang('en')}
                      data-testid="kora-preview-lang-en"
                    >EN</Button>
                    <Button
                      size="sm"
                      variant={previewLang === 'es' ? 'default' : 'outline'}
                      className="h-6 text-[10px] px-2"
                      onClick={() => setPreviewLang('es')}
                      data-testid="kora-preview-lang-es"
                    >ES</Button>
                  </div>
                </div>
                <div
                  className="h-96 overflow-y-auto space-y-2 bg-slate-50 rounded p-2 border border-slate-200"
                  data-testid="kora-preview-log"
                >
                  {previewLog.length === 0 ? (
                    <p className="text-[11px] text-slate-400 italic text-center pt-8">
                      Send a test message to see how Kora responds with your current draft.
                    </p>
                  ) : previewLog.map((m, i) => (
                    <div
                      key={i}
                      className={`text-xs rounded px-2 py-1.5 max-w-[85%] ${
                        m.role === 'user'
                          ? 'bg-[#0f2a5e] text-white ml-auto'
                          : 'bg-white border border-slate-200 text-slate-800'
                      }`}
                    >
                      {m.text}
                    </div>
                  ))}
                  {previewLoading && (
                    <div className="text-xs text-slate-500 italic">Kora is thinking…</div>
                  )}
                </div>
                <form
                  className="flex items-center gap-2"
                  onSubmit={(e) => { e.preventDefault(); runPreview(); }}
                >
                  <Input
                    value={previewInput}
                    onChange={(e) => setPreviewInput(e.target.value)}
                    placeholder={previewLang === 'es' ? 'Escribe un mensaje…' : 'Type a message…'}
                    disabled={previewLoading}
                    data-testid="kora-preview-input"
                  />
                  <Button
                    type="submit"
                    disabled={previewLoading || !previewInput.trim()}
                    className="bg-[#b8862d] hover:bg-[#a1751f]"
                    data-testid="kora-preview-send-btn"
                  >
                    <Send size={14} />
                  </Button>
                </form>
                <p className="text-[10px] text-slate-500">
                  Uses your unsaved draft. Nothing is stored on visitor lands until you hit <b>Save changes</b>.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

function TagListEditor({ value, onChange, placeholder, testId }) {
  const [input, setInput] = useState('');
  const add = () => {
    const v = input.trim();
    if (!v) return;
    if (!value.includes(v)) onChange([...(value || []), v]);
    setInput('');
  };
  const remove = (v) => onChange((value || []).filter((x) => x !== v));

  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {(value || []).length === 0 ? (
          <span className="text-[11px] text-slate-400 italic">Empty — using defaults.</span>
        ) : value.map((v) => (
          <Badge
            key={v}
            variant="secondary"
            className="pl-2 pr-1 py-1 text-[11px] flex items-center gap-1"
          >
            {v}
            <button
              type="button"
              className="text-slate-400 hover:text-slate-800"
              onClick={() => remove(v)}
              data-testid={`${testId}-remove-${v}`}
            >
              <X size={12} />
            </button>
          </Badge>
        ))}
      </div>
      <form
        className="flex gap-2"
        onSubmit={(e) => { e.preventDefault(); add(); }}
      >
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder}
          data-testid={`${testId}-input`}
        />
        <Button type="submit" variant="outline" data-testid={`${testId}-add-btn`}>
          Add
        </Button>
      </form>
    </div>
  );
}
